import { useState, useCallback, useEffect } from 'react';
import { extractPNGMetadata, extractSVGMetadata } from '@alchemiesolns/image-metadata';
import { decodeWorkspace, toConfigString } from '@alchemiesolns/workspace-serialization';
import type { AlchemieImportResult } from './useFileImport';
import type { ArrowType } from '../components/ArrowSection';
import type { TemplateMode } from '../components/BatchUpload';
import {
  clampSchemeZoomPercent,
  isSchemeFontSize,
  schemeZoomFromLegacyDisplayZoom,
  snapSchemeFontSize,
  type SchemeFontSize,
} from '../utils/schemeZoom';
import type { OverlayTextHorizontalAlign } from '../components/OverlayTextSvg';

export interface SchemeState {
  reactantConfig: string;
  productConfig: string;
}

export interface SchemeSetters {
  setTemplateMode: (mode: TemplateMode) => void;
  setReactantConfig: (config: string) => void;
  setProductConfig: (config: string) => void;
  setTopText: (text: string) => void;
  setBottomText: (text: string) => void;
  setArrowType: (type: ArrowType) => void;
  setAltText: (text: string) => void;
  setReactantViewBox: (viewBox: string | null) => void;
  setProductViewBox: (viewBox: string | null) => void;
  setReactantOverlayText: (text: string) => void;
  setProductOverlayText: (text: string) => void;
  setReactantOverlayPosition: (p: { x: number; y: number } | null) => void;
  setProductOverlayPosition: (p: { x: number; y: number } | null) => void;
  setReactantOverlayTextAlign: (a: OverlayTextHorizontalAlign | null) => void;
  setProductOverlayTextAlign: (a: OverlayTextHorizontalAlign | null) => void;
  setSchemeFontSize?: (size: SchemeFontSize) => void;
  setSchemeZoomPercent?: (percent: number) => void;
  /** Called when an import replaces scheme content (clears transient UI hints). */
  onSchemeReplaced?: () => void;
}

function overlayPositionFromData(v: unknown): { x: number; y: number } | null {
  if (!v || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  const x = o.x;
  const y = o.y;
  if (typeof x !== 'number' || typeof y !== 'number' || !Number.isFinite(x) || !Number.isFinite(y)) return null;
  return { x, y };
}

function overlayTextAlignFromData(v: unknown): OverlayTextHorizontalAlign | null {
  if (v === 'start' || v === 'middle' || v === 'end') return v;
  return null;
}

export function useSchemeImport(
  state: SchemeState,
  setters: SchemeSetters,
  showToastMessage: (message: string, durationMs?: number) => void,
) {
  const [pendingImport, setPendingImport] = useState<AlchemieImportResult | null>(null);

  /** Apply an alchemie import — sets all state fields from the imported data */
  const applyAlchemieImport = useCallback(({ data, meta }: AlchemieImportResult) => {
    setters.onSchemeReplaced?.();

    // Decode imported strings — handles both opaque (alchemie:ws:v1:...) and legacy config strings
    const toConfig = (input: unknown): string => {
      if (typeof input !== 'string' || !input) return '';
      try { return toConfigString(decodeWorkspace(input)); }
      catch { return input; } // fallback: use raw string if decode fails
    };

    const mode = data.templateMode as string | undefined;

    const importedReactantViewBox =
      typeof data.reactantViewBox === 'string' ? data.reactantViewBox : null;
    const importedProductViewBox =
      typeof data.productViewBox === 'string' ? data.productViewBox : null;
    const importedSchemeZoom =
      typeof data.schemeDisplayZoom === 'number' && Number.isFinite(data.schemeDisplayZoom)
        ? data.schemeDisplayZoom
        : null;
    const importedSchemeFont = isSchemeFontSize(data.schemeFontSize) ? data.schemeFontSize : null;
    const importedSchemePercent =
      typeof data.schemeZoomPercent === 'number' && Number.isFinite(data.schemeZoomPercent)
        ? Math.round(data.schemeZoomPercent)
        : null;
    const hasPersistedZoomUi =
      (importedSchemeFont !== null && importedSchemePercent !== null) || importedSchemeZoom !== null;
    const hasPersistedViewportState = Boolean(
      importedReactantViewBox || importedProductViewBox || hasPersistedZoomUi,
    );

    const importedReactOverlayPos = overlayPositionFromData(data.reactantOverlayPosition);
    const importedProductOverlayPos = overlayPositionFromData(data.productOverlayPosition);
    const importedReactOverlayAlign = overlayTextAlignFromData(data.reactantOverlayTextAlign);
    const importedProductOverlayAlign = overlayTextAlignFromData(data.productOverlayTextAlign);

    if (mode === 'molecule') {
      const rTxt = (data.reactantOverlayText as string) || '';
      setters.setTemplateMode('molecule');
      setters.setReactantConfig(toConfig(data.molecule));
      setters.setProductConfig('');
      setters.setReactantOverlayText(rTxt);
      setters.setProductOverlayText('');
      setters.setReactantOverlayPosition(importedReactOverlayPos);
      setters.setProductOverlayPosition(null);
      setters.setReactantOverlayTextAlign(
        !rTxt ? null : (importedReactOverlayAlign ?? (importedReactOverlayPos ? 'start' : 'middle')),
      );
      setters.setProductOverlayTextAlign(null);
    } else if (mode === 'reaction') {
      const rTxt = (data.reactantOverlayText as string) || '';
      const pTxt = (data.productOverlayText as string) || '';
      setters.setTemplateMode('reaction');
      setters.setReactantConfig(toConfig(data.reactant));
      setters.setProductConfig(toConfig(data.product));
      setters.setTopText((data.reagents as string) || '');
      setters.setBottomText((data.conditions as string) || '');
      setters.setArrowType((data.arrowType as ArrowType) || 'single');
      setters.setReactantOverlayText(rTxt);
      setters.setProductOverlayText(pTxt);
      setters.setReactantOverlayPosition(importedReactOverlayPos);
      setters.setProductOverlayPosition(importedProductOverlayPos);
      setters.setReactantOverlayTextAlign(
        !rTxt ? null : (importedReactOverlayAlign ?? (importedReactOverlayPos ? 'start' : 'end')),
      );
      setters.setProductOverlayTextAlign(
        !pTxt ? null : (importedProductOverlayAlign ?? 'start'),
      );
    } else {
      const config = toConfig(data.molecule || data.reactant || data.config);
      if (config) setters.setReactantConfig(config);
      if (data.product) setters.setProductConfig(toConfig(data.product));
      const rTxtLegacy = (data.reactantOverlayText as string) || '';
      const pTxtLegacy = (data.productOverlayText as string) || '';
      setters.setReactantOverlayText(rTxtLegacy);
      setters.setProductOverlayText(pTxtLegacy);
      setters.setReactantOverlayPosition(importedReactOverlayPos);
      setters.setProductOverlayPosition(importedProductOverlayPos);
      setters.setReactantOverlayTextAlign(
        !rTxtLegacy ? null : (importedReactOverlayAlign ?? (importedReactOverlayPos ? 'start' : 'end')),
      );
      setters.setProductOverlayTextAlign(
        !pTxtLegacy ? null : (importedProductOverlayAlign ?? 'start'),
      );
    }

    if (meta.description) setters.setAltText(meta.description);
    else if (meta.title) setters.setAltText(meta.title); // legacy fallback

    setters.setReactantViewBox(importedReactantViewBox);
    setters.setProductViewBox(importedProductViewBox);
    if (!hasPersistedViewportState) {
      // Backward compatibility: older exports lacked viewport metadata.
      // Reset to default fixed framing in the current single-mode UI.
      setters.setReactantViewBox(null);
      setters.setProductViewBox(null);
    }
    if (importedSchemeZoom !== null) {
      const { fontSize, percent } = schemeZoomFromLegacyDisplayZoom(importedSchemeZoom);
      setters.setSchemeFontSize?.(fontSize);
      setters.setSchemeZoomPercent?.(percent);
    } else if (importedSchemeFont !== null && importedSchemePercent !== null) {
      // Older metadata could couple font and percent into effective zoom.
      // Preserve visual scale by migrating via the legacy effective formula.
      const legacyEffectiveZoom = (importedSchemeFont / 12) * (importedSchemePercent / 100);
      const percent = clampSchemeZoomPercent(12, Math.round(legacyEffectiveZoom * 100));
      const fontSize = snapSchemeFontSize((percent / 100) * 12);
      setters.setSchemeFontSize?.(fontSize);
      setters.setSchemeZoomPercent?.(percent);
    }

    showToastMessage('Scheme imported successfully');
  }, [setters, showToastMessage]);

  /** Handle alchemie import — confirm first if existing data would be replaced */
  const handleAlchemieImport = useCallback((result: AlchemieImportResult) => {
    const hasExistingData = state.reactantConfig || state.productConfig;
    if (hasExistingData) {
      setPendingImport(result);
    } else {
      applyAlchemieImport(result);
    }
  }, [state.reactantConfig, state.productConfig, applyAlchemieImport]);

  const confirmImport = useCallback(() => {
    if (pendingImport) {
      applyAlchemieImport(pendingImport);
      setPendingImport(null);
    }
  }, [pendingImport, applyAlchemieImport]);

  const cancelImport = useCallback(() => {
    setPendingImport(null);
  }, []);

  // Global drop handler: prevent browser file navigation and attempt metadata import.
  useEffect(() => {
    const handleGlobalDragOver = (e: DragEvent) => { e.preventDefault(); };
    const handleGlobalDrop = async (e: DragEvent) => {
      e.preventDefault();
      const files = e.dataTransfer?.files;
      if (!files || files.length === 0) return;

      const file = files[0];
      const name = file.name.toLowerCase();

      try {
        if (name.endsWith('.png') || file.type === 'image/png') {
          const buffer = new Uint8Array(await file.arrayBuffer());
          const meta = extractPNGMetadata(buffer);
          if (meta.alchemieData) {
            handleAlchemieImport({ data: meta.alchemieData, meta, sourceFilename: file.name });
            return;
          }
        }

        if (name.endsWith('.svg') || file.type === 'image/svg+xml') {
          const text = await file.text();
          const meta = extractSVGMetadata(text);
          if (meta.alchemieData) {
            handleAlchemieImport({ data: meta.alchemieData, meta, sourceFilename: file.name });
            return;
          }
        }
      } catch {
        // Parse error — ignore silently
      }
    };

    document.addEventListener('dragover', handleGlobalDragOver);
    document.addEventListener('drop', handleGlobalDrop);
    return () => {
      document.removeEventListener('dragover', handleGlobalDragOver);
      document.removeEventListener('drop', handleGlobalDrop);
    };
  }, [handleAlchemieImport]);

  return {
    pendingImport,
    handleAlchemieImport,
    confirmImport,
    cancelImport,
  };
}
